import { formatValueToCurrency } from '@helpers/formatCentToAmount.helper'
import { Box, Stack, Typography } from '@mui/material'
import { ISingleDocument } from '@typings'
import dayjs from 'dayjs'
import { StatusBadge } from 'src/components/features/status-badge/StatusBadge'

export interface IMobileDocumentRow extends ISingleDocument {
  onRowClick?: (id: ISingleDocument['id']) => void
}

export const MobileDocumentRow = ({
  document_number,
  subject,
  issued_at,
  client,
  total = 0,
  id,
  lifecycle_status,
  payment_status,
  quotation_status,
  type,
  onRowClick,
  ...rest
}: IMobileDocumentRow) => {
  const handleRowClick = (id: ISingleDocument['id']) => {
    if (onRowClick) onRowClick(id)
  }

  // Show document number if available, otherwise show subject (for draft quotations)
  const displayTitle = document_number || subject

  return (
    <Box
      sx={({ palette }) => ({
        width: '100%',
        borderBottom: '1px solid',
        borderBottomColor: palette.divider,
        boxSizing: 'border-box',
        cursor: 'pointer',
        p: 2,
        '&:hover': {
          backgroundColor: palette.background.default,
        },
      })}
      onClick={() => handleRowClick({ id } as any)}
      {...rest}>
      <Stack spacing={0.5}>
        {/* Row 1: Document number + Status badges | Amount */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ minWidth: 0, flex: 1 }}
            flexWrap="wrap">
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600 }}>
              {displayTitle}
            </Typography>
            {/* Show lifecycle for drafts, payment status for invoices, quotation status for quotations */}
            {lifecycle_status === 'draft' && (
              <StatusBadge
                status={lifecycle_status}
                type="lifecycle"
                size="small"
              />
            )}
            {type === 'invoice' && lifecycle_status === 'finalized' && payment_status && (
              <StatusBadge
                status={payment_status}
                type="payment"
                size="small"
              />
            )}
            {type === 'quotation' && lifecycle_status === 'finalized' && quotation_status && (
              <StatusBadge
                status={quotation_status}
                type="quotation"
                size="small"
              />
            )}
          </Stack>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, flexShrink: 0 }}>
            {formatValueToCurrency(total)}
          </Typography>
        </Stack>

        {/* Row 2: Date */}
        <Typography
          variant="caption"
          color="text.secondary">
          {issued_at && dayjs(issued_at).format('DD-MM-YYYY')}
        </Typography>

        {/* Row 3: Client name */}
        <Typography variant="body2">
          {client.name}
        </Typography>
      </Stack>
    </Box>
  )
}
